import { REMOTE_NAMES, SETTINGS_CLOSE_EVENT } from '@host/mf'
import { NavBar, Sheet } from '@prismeet/react'
import { useEffect, useMemo } from 'react'
import { Link as RouterLink, Outlet, useLocation } from 'react-router'

import { RemoteOutlet } from '@/shared/RemoteOutlet'
import { useAppUiStore } from '@/shared/stores/appUiStore'

import { AppRail } from './AppRail'
import { AppSidebar } from './AppSidebar'
import { AppTopBar } from './AppTopBar'
import { MOBILE_NAV_LABEL, NAV_ITEMS, SIDEBAR_LABEL } from './constants'
import styles from './index.module.scss'
import { RightAside } from './RightAside'

/**
 * Оболочка соцсети.
 *
 * Хост владеет рамкой, микрофронты — содержимым центральной колонки.
 * Всё, что видно вокруг `Outlet`, переживает переход между микрофронтами
 * и не перерисовывается: навигация не должна моргать оттого, что
 * пользователь перешёл из ленты в спейсы.
 */
export function AppLayout() {
  const location = useLocation()
  const sidebarOpen = useAppUiStore((state) => state.sidebarOpen)
  const setSidebarOpen = useAppUiStore((state) => state.setSidebarOpen)
  const settingsOpen = useAppUiStore((state) => state.settingsOpen)
  const setSettingsOpen = useAppUiStore((state) => state.setSettingsOpen)

  // Переход закрывает шторку. Без этого пользователь, выбравший раздел,
  // остаётся смотреть на меню поверх того, что он только что открыл.
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname, setSidebarOpen])

  // Ремоут настроек не может импортировать стор хоста (@host/mf не в shared),
  // поэтому «закрой меня» приходит DOM-событием — контракт SETTINGS_CLOSE_EVENT.
  useEffect(() => {
    const onSettingsClose = () => {
      setSettingsOpen(false)
    }
    window.addEventListener(SETTINGS_CLOSE_EVENT, onSettingsClose)
    return () => {
      window.removeEventListener(SETTINGS_CLOSE_EVENT, onSettingsClose)
    }
  }, [setSettingsOpen])

  const activeValue = useMemo(() => {
    const match = NAV_ITEMS.find(
      (item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`),
    )
    return match?.value ?? 'feed'
  }, [location.pathname])

  const mobileItems = useMemo(
    () =>
      NAV_ITEMS.filter((item) => item.mobile).map(({ value, label, to, Icon }) => ({
        value,
        label,
        icon: <Icon />,
        as: RouterLink,
        to,
      })),
    [],
  )

  return (
    <div className={styles.root}>
      {/*
        Колонка рельса отдана микрофронту sidebar: список серверов
        в стиле Discord. Пока ремоут не зарегистрирован или не отвечает,
        оболочка откатывается к встроенному AppRail — хост обязан
        выглядеть осмысленно и с нулём микрофронтов.
      */}
      <div className={styles.railSlot}>
        <RemoteOutlet name={REMOTE_NAMES.sidebar} placeholder={<AppRail />} />
      </div>

      <div className={styles.sidebar}>
        <AppSidebar />
      </div>

      <div className={styles.main}>
        <AppTopBar />
        <main className={styles.content}>
          <Outlet />
        </main>
      </div>

      <RightAside />

      <Sheet
        open={sidebarOpen}
        onClose={() => {
          setSidebarOpen(false)
        }}
        title={SIDEBAR_LABEL}
        placement="start"
      >
        <AppSidebar />
      </Sheet>

      <div className={styles.mobileNav}>
        <NavBar items={mobileItems} value={activeValue} label={MOBILE_NAV_LABEL} />
      </div>

      {/*
        Настройки в стиле Discord: смонтирован — значит открыт. Готовый
        ремоут рисует скрим и окно порталом в body, а этот слой центрирует
        только служебные состояния RemoteOutlet — загрузку, ошибку
        и подсказку «ремоут не зарегистрирован».
      */}
      {settingsOpen && (
        <div className={styles.settingsLayer}>
          <RemoteOutlet name={REMOTE_NAMES.settings} />
        </div>
      )}
    </div>
  )
}
